import { useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import {
  VERTICALS,
  SERVICE_CATALOGUE,
  resolveVerticalFromPath,
  buildWhatsAppUrl,
  buildTelUrl,
} from '@/config/contactChannels';

/**
 * useContactChannel — derives the correct contact-channel config for the
 * currently active route. Pure route-based; no visitor/UA/IP detection.
 *
 * Returns an object with:
 *   vertical          : 'recruitment' | 'study_abroad'
 *   label             : human label
 *   leadServiceTag    : CRM source tag for forms
 *   phoneDisplay      : "+91 99588 48998" — for visible UI
 *   phoneDisplayShort : "+91-9958848998"
 *   phoneE164         : "+919958848998"
 *   phoneTelUrl       : "tel:+919958848998"
 *   whatsappDisplay   : "+91 99588 48998"
 *   whatsappUrl       : "https://wa.me/919958848998?text=..."
 *   whatsappUrlWith(t): function that builds wa.me with a custom message
 *   email             : vertical's inbox
 *   adsLabelEnv       : env-key map for Google Ads conversion labels
 *
 * Usage:
 *   const ch = useContactChannel();
 *   <a href={ch.phoneTelUrl}>{ch.phoneDisplay}</a>
 *   <a href={ch.whatsappUrlWith('Hello, I am from the UK hub')}>WhatsApp</a>
 */
export const useContactChannel = () => {
  const { pathname } = useLocation();

  return useMemo(() => {
    const verticalKey = resolveVerticalFromPath(pathname);
    const ch = VERTICALS[verticalKey];
    return {
      vertical:          ch.key,
      label:             ch.label,
      leadServiceTag:    ch.leadServiceTag,
      phoneRaw:          ch.phoneRaw,
      phoneDisplay:      ch.phoneDisplay,
      phoneDisplayShort: ch.phoneDisplayShort,
      phoneE164:         ch.phoneE164,
      phoneTelUrl:       buildTelUrl(ch),
      whatsappRaw:       ch.whatsappRaw,
      whatsappDisplay:   ch.whatsappDisplay,
      whatsappGreeting:  ch.whatsappGreeting,
      whatsappUrl:       buildWhatsAppUrl(ch),
      whatsappUrlWith:   (text) => buildWhatsAppUrl(ch, text),
      email:             ch.email,
      adsLabelEnv:       ch.adsLabelEnv,
      // Centralised, vertical-scoped service catalogue for this route.
      serviceTypes:      SERVICE_CATALOGUE[verticalKey] || [],
    };
  }, [pathname]);
};

export default useContactChannel;
